import "@dxos/node-std/globals"

// packages/core/echo/document-model/src/document-model.ts
import get2 from "lodash.get";
import assert2 from "@dxos/node-std/assert";
import { Model } from "@dxos/model-factory";
import { schema } from "@dxos/protocols";
import { ObjectMutation as ObjectMutation2 } from "@dxos/protocols/proto/dxos/echo/model/document";

// packages/core/echo/document-model/src/mutation.ts
import get from "lodash.get";
import set from "lodash.set";
import assert from "@dxos/node-std/assert";
import { ObjectMutation } from "@dxos/protocols/proto/dxos/echo/model/document";

// packages/core/echo/document-model/src/ordered-array.ts
import * as Y from "yjs";

// packages/core/echo/document-model/src/reference.ts
var Reference = class {
  static fromValue(value) {
    return new Reference(value.itemId);
  }
  // prettier-ignore
  constructor(itemId) {
    this.itemId = itemId;
  }
  encode() {
    return {
      itemId: this.itemId
    };
  }
};

// packages/core/echo/document-model/src/ordered-array.ts
var ARRAY_KEY = "a";
var REFERENCE_KEY = "@reference";
var encodeValue = (value) => {
  if (value instanceof Reference) {
    return Object.fromEntries([
      [
        REFERENCE_KEY,
        value.encode()
      ]
    ]);
  } else if (Array.isArray(value)) {
    return value.map((value2) => encodeValue(value2));
  } else if (typeof value === "object" && value !== null) {
    return Object.fromEntries(Object.entries(value).map(([key, value2]) => {
      return [
        key,
        encodeValue(value2)
      ];
    }));
  }
  return value;
};
var decodeValue = (value) => {
  if (Array.isArray(value)) {
    return value.map((value2) => decodeValue(value2));
  } else if (typeof value === "object" && value !== null) {
    if (REFERENCE_KEY in value) {
      return Reference.fromValue(value[REFERENCE_KEY]);
    }
    return Object.fromEntries(Object.entries(value).map(([key, value2]) => {
      return [
        key,
        decodeValue(value2)
      ];
    }));
  }
  return value;
};
var OrderedArray = class {
  static fromSnapshot(snapshot) {
    const doc = new Y.Doc();
    Y.applyUpdateV2(doc, snapshot.payload);
    return new OrderedArray(doc, doc.getArray(ARRAY_KEY));
  }
  static fromValues(values) {
    const doc = new Y.Doc();
    const array = doc.getArray(ARRAY_KEY);
    array.insert(0, encodeValue(values));
    return new OrderedArray(doc, array);
  }
  // prettier-ignore
  constructor(doc, array) {
    this.doc = doc;
    this.array = array;
  }
  insert(index, content) {
    this.array.insert(index, encodeValue(content));
  }
  delete(index, length) {
    this.array.delete(index, length);
  }
  push(content) {
    this.array.push(encodeValue(content));
  }
  unshift(content) {
    this.array.unshift(encodeValue(content));
  }
  encodeSnapshot() {
    return {
      id: new Uint8Array(),
      payload: Y.encodeStateAsUpdateV2(this.doc)
    };
  }
  toArray() {
    return decodeValue(this.array.toArray());
  }
  get(index) {
    return decodeValue(this.array.get(index));
  }
  transact(tx) {
    let updateReceived;
    const cb = (update) => {
      updateReceived = {
        id: new Uint8Array(),
        payload: update
      };
    };
    try {
      this.doc.once("updateV2", cb);
      this.doc.transact(() => {
        tx();
      });
    } finally {
      this.doc.off("updateV2", cb);
    }
    if (updateReceived) {
      return updateReceived;
    } else {
      return {
        id: new Uint8Array(),
        payload: new Uint8Array()
      };
    }
  }
  apply(mutation) {
    if (mutation.payload.byteLength > 0) {
      Y.applyUpdateV2(this.doc, mutation.payload);
    }
  }
};

// packages/core/echo/document-model/src/util.ts
var validateKey = (key) => {
  const parts = key.split(".");
  const valid = parts.every((part) => part.match(/^@?[$\w]+$/));
  if (!valid) {
    throw new Error(`Invalid key: ${key}`);
  }
  return key;
};
var removeKey = (object, key) => {
  const parts = key.split(".");
  let sub = object;
  if (parts.length > 1) {
    for (let i = 0; i < parts.length; i++) {
      const key1 = parts[i];
      if (i === parts.length - 1) {
        delete sub[key1];
      } else {
        sub = sub[key1];
      }
    }
  } else {
    delete object[key];
  }
  return object;
};

// packages/core/echo/document-model/src/mutation.ts
var Type;
(function(Type2) {
  Type2["NULL"] = "null";
  Type2["BOOLEAN"] = "bool";
  Type2["INTEGER"] = "int";
  Type2["FLOAT"] = "float";
  Type2["STRING"] = "string";
  Type2["BYTES"] = "bytes";
  Type2["TIMESTAMP"] = "timestamp";
  Type2["DATETIME"] = "datetime";
  Type2["REFERENCE"] = "reference";
  Type2["OBJECT"] = "object";
  Type2["YJS"] = "yjs";
})(Type || (Type = {}));
var SCALAR_TYPES = [
  Type.BOOLEAN,
  Type.INTEGER,
  Type.FLOAT,
  Type.STRING,
  Type.BYTES,
  Type.TIMESTAMP,
  Type.DATETIME
];
var KeyValueUtil = class {
  static createMessage(key, value) {
    assert(key);
    return {
      key,
      // eslint-disable-next-line no-use-before-define
      value: ValueUtil.createMessage(value)
    };
  }
};
var ValueUtil = class {
  /**
  * @param {any} value
  * @return {{Value}}
  */
  static createMessage(value) {
    if (value === null) {
      return {
        [Type.NULL]: true
      };
    } else if (typeof value === "boolean") {
      return ValueUtil.bool(value);
    } else if (typeof value === "number") {
      return value % 1 === 0 ? ValueUtil.integer(value) : ValueUtil.float(value);
    } else if (typeof value === "string") {
      return ValueUtil.string(value);
    } else if (value instanceof Uint8Array || Buffer.isBuffer(value)) {
      return ValueUtil.bytes(value);
    } else if (value instanceof Reference) {
      return ValueUtil.reference(value);
    } else if (value instanceof OrderedArray) {
      return ValueUtil.orderedArray(value);
    } else if (typeof value === "object") {
      return ValueUtil.object(value);
    } else {
      throw new Error(`Invalid value: ${value}`);
    }
  }
  static valueOf(value) {
    if (value.object !== void 0) {
      return ValueUtil.getObjectValue(value.object);
    }
    if (value.array !== void 0) {
      return value.array.values.map((value2) => ValueUtil.valueOf(value2));
    }
    if (value.int) {
      return parseInt(value.int);
    }
    const type = SCALAR_TYPES.find((type2) => value[type2] !== void 0);
    if (type) {
      return value[type];
    }
    return void 0;
  }
  static bytes(value) {
    return {
      [Type.BYTES]: value
    };
  }
  static bool(value) {
    return {
      [Type.BOOLEAN]: value
    };
  }
  static integer(value) {
    return {
      [Type.INTEGER]: value.toString()
    };
  }
  static float(value) {
    return {
      [Type.FLOAT]: value
    };
  }
  static string(value) {
    return {
      [Type.STRING]: value
    };
  }
  static datetime(value) {
    return {
      [Type.DATETIME]: value
    };
  }
  static reference(value) {
    return {
      [Type.REFERENCE]: value.encode()
    };
  }
  static orderedArray(value) {
    return {
      [Type.YJS]: value.encodeSnapshot()
    };
  }
  static object(value) {
    return {
      [Type.OBJECT]: {
        properties: Object.keys(value).map((key) => KeyValueUtil.createMessage(key, value[key]))
      }
    };
  }
  // TODO(burdon): Refactor.
  static getObjectValue(value) {
    const nestedObject = {};
    const { properties } = value;
    (properties != null ? properties : []).forEach(({ key, value: value2 }) => ValueUtil.applyValue(nestedObject, key, value2));
    return nestedObject;
  }
  static getScalarValue(value) {
    const type = SCALAR_TYPES.find((field) => value[field] !== void 0);
    if (type) {
      return value[type];
    }
  }
  static applyKeyValue(object, keyValue) {
    const { key, value } = keyValue;
    return ValueUtil.applyValue(object, key, value);
  }
  static applyValue(object, key, value) {
    assert(object);
    assert(key);
    if (value === void 0) {
      return removeKey(object, key);
    }
    if (value[Type.OBJECT]) {
      set(object, key, ValueUtil.getObjectValue(value[Type.OBJECT]));
      return object;
    }
    if (value[Type.NULL]) {
      set(object, key, null);
      return object;
    }
    const refValue = value[Type.REFERENCE];
    if (refValue !== void 0) {
      set(object, key, Reference.fromValue(refValue));
      return object;
    }
    const intValue = value[Type.INTEGER];
    if (intValue !== void 0) {
      set(object, key, parseInt(intValue));
      return object;
    }
    const scalar = ValueUtil.getScalarValue(value);
    if (scalar !== void 0) {
      set(object, key, scalar);
      return object;
    }
    if (value[Type.YJS]) {
      set(object, key, OrderedArray.fromSnapshot(value[Type.YJS]));
      return object;
    }
    throw new Error(`Unhandled value: ${JSON.stringify(value)}`);
  }
};
var MutationUtil = class {
  static applyMutationSet(object, message) {
    assert(message);
    if (message.type) {
      object.type = message.type;
    }
    const { mutations } = message;
    mutations == null ? void 0 : mutations.forEach((mutation) => MutationUtil.applyMutation(object.data, mutation));
    return object;
  }
  static applyMutation(object, mutation) {
    assert(object);
    const { operation = ObjectMutation.Operation.SET, key, value } = mutation;
    switch (operation) {
      case ObjectMutation.Operation.SET: {
        ValueUtil.applyValue(object, key, value);
        break;
      }
      case ObjectMutation.Operation.DELETE: {
        removeKey(object, key);
        break;
      }
      case ObjectMutation.Operation.ARRAY_PUSH: {
        const values = get(object, key, []);
        values.push(ValueUtil.valueOf(value));
        set(object, key, values);
        break;
      }
      case ObjectMutation.Operation.SET_ADD: {
        const values1 = new Set(get(object, key, []));
        values1.add(ValueUtil.valueOf(value));
        set(object, key, Array.from(values1.values()));
        break;
      }
      case ObjectMutation.Operation.SET_DELETE: {
        const values2 = new Set(get(object, key, []));
        values2.delete(ValueUtil.valueOf(value));
        set(object, key, Array.from(values2.values()));
        break;
      }
      case ObjectMutation.Operation.YJS: {
        const array = get(object, key, void 0);
        if (!(array instanceof OrderedArray)) {
          break;
        }
        array.apply(mutation.mutation);
        break;
      }
      default: {
        throw new Error(`Operation not implemented: ${operation}`);
      }
    }
    return object;
  }
  /**
  * Create single field mutation.
  */
  static createFieldMutation(key, value) {
    return value === void 0 ? {
      // NOTE: `null` is a legitimate value.
      operation: ObjectMutation.Operation.DELETE,
      key
    } : {
      operation: ObjectMutation.Operation.SET,
      key,
      value: ValueUtil.createMessage(value)
    };
  }
  /**
  * Create field mutations.
  */
  static createMultiFieldMutation(object) {
    return Object.entries(object).map(([key, value]) => MutationUtil.createFieldMutation(key, value));
  }
};

// packages/core/echo/document-model/src/document-model.ts
var DocumentModelStateMachine = class {
  constructor() {
    this._object = {
      data: {}
    };
  }
  getState() {
    return this._object;
  }
  reset(snapshot) {
    assert2(snapshot.root);
    const object = {
      data: {}
    };
    ValueUtil.applyValue(object, "data", snapshot.root);
    this._object = object;
    this._object.type = snapshot.type;
  }
  process(mutation) {
    MutationUtil.applyMutationSet(this._object, mutation);
  }
  snapshot() {
    return {
      root: ValueUtil.createMessage(this._object.data),
      type: this._object.type
    };
  }
};
var MutationBuilder = class {
  // prettier-ignore
  constructor(_model) {
    this._model = _model;
    this._mutations = [];
  }
  set(key, value) {
    this._mutations.push(MutationUtil.createFieldMutation(key, value));
    return this;
  }
  _yjsTransact(key, tx) {
    assert2(this._model);
    const arrayInstance = this._model.get(key);
    assert2(arrayInstance instanceof OrderedArray);
    const mutation = arrayInstance.transact(() => {
      tx(arrayInstance);
    });
    this._mutations.push({
      operation: ObjectMutation2.Operation.YJS,
      key,
      mutation
    });
    return this;
  }
  arrayInsert(key, index, content) {
    return this._yjsTransact(key, (arrayInstance) => {
      arrayInstance.insert(index, content);
    });
  }
  arrayDelete(key, index, length) {
    return this._yjsTransact(key, (arrayInstance) => {
      arrayInstance.delete(index, length);
    });
  }
  arrayPush(key, content) {
    return this._yjsTransact(key, (arrayInstance) => {
      arrayInstance.push(content);
    });
  }
  arrayUnshift(key, content) {
    return this._yjsTransact(key, (arrayInstance) => {
      arrayInstance.unshift(content);
    });
  }
  async commit() {
    assert2(this._model);
    return this._model._makeMutation({
      mutations: this._mutations
    });
  }
  /**
  * Returns a mutation object without applying it.
  */
  build() {
    return {
      mutations: this._mutations
    };
  }
};
var DocumentModel = class extends Model {
  get type() {
    return this._getState().type;
  }
  /**
  * Returns an immutable object.
  */
  toObject() {
    return this._getState().data;
  }
  builder() {
    return new MutationBuilder(this);
  }
  get(key, defaultValue) {
    validateKey(key);
    return get2(this._getState().data, key, defaultValue);
  }
  async set(key, value) {
    validateKey(key);
    await this._makeMutation({
      mutations: [
        MutationUtil.createFieldMutation(key, value)
      ]
    });
  }
  /**
  * @deprecated
  */
  // TODO(burdon): Remove.
  getProperty(key, defaultValue = void 0) {
    return this.get(key, defaultValue);
  }
  /**
  * @deprecated
  */
  // TODO(burdon): Remove.
  async setProperty(key, value) {
    await this.set(key, value);
  }
  /**
  * @deprecated
  */
  // TODO(burdon): Remove.
  async setProperties(properties) {
    await this._makeMutation({
      mutations: MutationUtil.createMultiFieldMutation(properties)
    });
  }
  async addToSet(key, value) {
    await this._makeMutation({
      mutations: [
        {
          operation: ObjectMutation2.Operation.SET_ADD,
          key,
          value: ValueUtil.createMessage(value)
        }
      ]
    });
  }
  async removeFromSet(key, value) {
    await this._makeMutation({
      mutations: [
        {
          operation: ObjectMutation2.Operation.SET_DELETE,
          key,
          value: ValueUtil.createMessage(value)
        }
      ]
    });
  }
  async pushToArray(key, value) {
    await this._makeMutation({
      mutations: [
        {
          operation: ObjectMutation2.Operation.ARRAY_PUSH,
          key,
          value: ValueUtil.createMessage(value)
        }
      ]
    });
  }
  /**
  * @internal
  */
  async _makeMutation(mutation) {
    const receipt = await this.write(mutation);
    await receipt.waitToBeProcessed();
  }
};
DocumentModel.meta = {
  type: "dxos:model/document",
  stateMachine: () => new DocumentModelStateMachine(),
  mutationCodec: schema.getCodecForType("dxos.echo.model.document.ObjectMutationSet"),
  snapshotCodec: schema.getCodecForType("dxos.echo.model.document.ObjectSnapshot")
};

// packages/core/echo/document-model/src/matcher.ts
import { Predicate } from "@dxos/protocols/proto/dxos/echo/model/document";
var Matcher = class {
  // prettier-ignore
  constructor(_options) {
    this._options = _options;
  }
  getFilter(query) {
    return (item) => this._matchItem(item, query.root);
  }
  /**
  * Returns list of matched items.
  */
  // TODO(burdon): Async API?
  matchItems(query, items) {
    return items.filter((item) => this._matchItem(item, query.root));
  }
  /**
  * Recursively match predicate tree against current item.
  */
  _matchItem(item, predicate) {
    var _a, _b;
    const { getter } = this._options;
    switch (predicate.op) {
      case Predicate.Operation.OR: {
        return predicate.predicates.findIndex((predicate2) => this._matchItem(item, predicate2)) !== -1;
      }
      case Predicate.Operation.AND: {
        return predicate.predicates.findIndex((predicate2) => !this._matchItem(item, predicate2)) === -1;
      }
      case Predicate.Operation.NOT: {
        return predicate.predicates.findIndex((predicate2) => !this._matchItem(item, predicate2)) !== -1;
      }
      case Predicate.Operation.IN: {
        const values = ValueUtil.valueOf(predicate.value) || [];
        const value = getter(item, predicate.key);
        return value && values.indexOf(value) !== -1;
      }
      case Predicate.Operation.EQUALS: {
        const value1 = getter(item, predicate.key);
        return value1 === ValueUtil.valueOf(predicate.value);
      }
      case Predicate.Operation.PREFIX_MATCH: {
        const value2 = getter(item, predicate.key);
        if (typeof value2 === "string") {
          const match = (_a = ValueUtil.valueOf(predicate.value)) == null ? void 0 : _a.toLowerCase();
          return match && value2.toLowerCase().indexOf(match) === 0;
        }
        break;
      }
      case Predicate.Operation.TEXT_MATCH: {
        const text = (_b = ValueUtil.valueOf(predicate.value)) == null ? void 0 : _b.trim().toLowerCase();
        if (!text) {
          break;
        }
        if (this._options.textIndex) {
          const matches = this._options.textIndex.search(text);
          return matches.findIndex((match) => match.id === item.id) !== -1;
        }
        const value3 = getter(item, predicate.key);
        if (typeof value3 === "string") {
          const words = value3.toLowerCase().split(/\s+/);
          return words.findIndex((word) => word.indexOf(text) === 0) !== -1;
        }
        break;
      }
    }
    return false;
  }
};

// packages/core/echo/document-model/src/text-index.ts
import MiniSearch from "minisearch";
var TextIndex = class {
  constructor({ fields, getter }) {
    this._cache = /* @__PURE__ */ new Map();
    this._items = [];
    this._minisearch = new MiniSearch({
      idField: "id",
      fields,
      extractField: getter
    });
  }
  // TODO(burdon): Monitor memory usage and timing.
  // TODO(burdon): Calcuate diff.
  update(items) {
    this._items = items;
    this._minisearch.removeAll();
    this._minisearch.addAll(items);
    this._cache.clear();
    return this;
  }
  // TODO(burdon): Async API?
  search(text) {
    let results = this._cache.get(text);
    if (!results) {
      results = this._minisearch.search(text).map((result) => ({
        ...result,
        item: this._items.find((item) => item.id === result.id)
      }));
      this._cache.set(text, results);
    }
    return results;
  }
};
export {
  DocumentModel,
  KeyValueUtil,
  Matcher,
  MutationBuilder,
  MutationUtil,
  OrderedArray,
  Reference,
  TextIndex,
  ValueUtil,
  removeKey,
  validateKey
};
//# sourceMappingURL=index.mjs.map
